import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { AuthButton } from '@/core/permissions/AuthButton';
import { CategoryForm } from '../shared/components/CategoryForm';
import { useCategoriesEdit } from './hooks/useCategoriesEdit';
import type { CategoryFormValues } from '../shared/types/categories.types';

export function CategoriesEditPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const { category, initialValues, parentOptions, isLoading, updateCategory } = useCategoriesEdit(id);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  useEffect(() => {
    if (!id || (!isLoading && !category)) {
      toast.error('Không tìm thấy danh mục');
      navigate('?tab=categories');
    }
  }, [id, category, isLoading, navigate]);

  const handleSubmit = async (values: CategoryFormValues) => {
    try {
      await updateCategory(values);
      toast.success('Cập nhật danh mục thành công');
      navigate('?tab=categories');
    } catch (error: any) {
      const message = error?.message || error?.details || 'Không thể cập nhật danh mục';
      toast.error(message);
    }
  };

  if (isLoading && !category) {
    return (
      <div className="flex h-64 items-center justify-center rounded-md p-3">
        <i className="ri-loader-4-line animate-spin text-2xl text-emerald-700" />
      </div>
    );
  }

  return (
    <div className="space-y-2 p-3">
      <section className="rounded-md border border-slate-100 bg-white p-3 shadow-sm">
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <button
                type="button"
                onClick={() => setShowCancelConfirm(true)}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              >
                <i className="ri-arrow-left-line" />
              </button>
              <div className="min-w-0">
                <h1 className="truncate text-lg font-semibold text-slate-900">Chỉnh sửa danh mục</h1>
                <p className="truncate text-sm text-slate-500">{category?.name || ''}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {category && initialValues ? (
        <CategoryForm
          id="category-edit-form"
          initialValues={initialValues}
          lookups={{ parentOptions }}
          onSubmit={handleSubmit}
          isLoading={isLoading}
        />
      ) : null}

      <ConfirmDialog
        isOpen={showCancelConfirm}
        title="Xác nhận thoát"
        message="Mọi thay đổi chưa lưu sẽ bị mất."
        confirmText="Thoát"
        cancelText="Tiếp tục sửa"
        isDestructive
        onConfirm={() => navigate('?tab=categories')}
        onClose={() => setShowCancelConfirm(false)}
      />

      {/* Sticky Footer */}
      <div className="fixed bottom-0 left-0 right-0 z-50 flex items-center justify-end gap-3 border-t border-slate-200 bg-white p-4 shadow-lg lg:left-[240px]">
        <button
          type="button"
          onClick={() => setShowCancelConfirm(true)}
          disabled={isLoading}
          className="h-10 rounded-md border border-slate-200 bg-white px-6 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
        >
          Hủy bỏ
        </button>
        <AuthButton
          module="settings.categories"
          action="update"
          behavior="disable"
          form="category-edit-form"
          type="submit"
          disabled={isLoading}
          className="flex h-10 items-center justify-center gap-2 rounded-md bg-emerald-600 px-6 text-sm font-medium text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
        >
          {isLoading ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" /> : <i className="ri-save-line" />}
          <span>{isLoading ? 'Đang lưu...' : 'Cập nhật danh mục'}</span>
        </AuthButton>
      </div>
    </div>
  );
}

